import { useState, useMemo, useEffect, useRef } from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Chip from '@mui/material/Chip';
import TextField from '@mui/material/TextField';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import StopIcon from '@mui/icons-material/Stop';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import DownloadIcon from '@mui/icons-material/Download';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { modalHeaderBtnSx } from './chatMessageStyles';
import Tooltip from '@mui/material/Tooltip';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import CodeIcon from '@mui/icons-material/Code';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TranscriptView from './components/TranscriptView';
import ListeningVisualizer from './components/ListeningVisualizer';
import WordTicker from './components/WordTicker';
import ProcessingOverlay from './components/ProcessingOverlay';
import SessionHistory from './components/SessionHistory';
import useMeetingSession from './hooks/useMeetingSession';
import { API_BASE_URL, getAuthHeaders } from './auth';
import { useTranslation } from 'react-i18next';

const SPEAKER_COLORS = ['#19d0ef', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899'];

export default function ExpandableMeetingMinutes({ title, icon, expanded, language, modal, onClose }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const effectiveExpanded = expanded || isExpanded;
  const { t } = useTranslation();

  const {
    startSession,
    stopSession,
    sessionState,
    transcriptSegments,
    transcriptVersion,
    partialText,
    minutes,
    error,
    sessionId,
    sessionTitle,
    setSessionTitle,
  } = useMeetingSession();

  const isConnecting = sessionState === 'connecting';
  const isRecording = sessionState === 'recording';
  const isRediarizing = sessionState === 'rediarizing';
  const isGenerating = sessionState === 'generating_minutes';
  const isCompleted = sessionState === 'completed';

  // Speaker name mapping: { speakerId: displayName }
  const [speakerNames, setSpeakerNames] = useState({});
  const [editingSpeaker, setEditingSpeaker] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleEditValue, setTitleEditValue] = useState('');

  // Detect unique speakers from transcript segments
  const detectedSpeakers = useMemo(() => {
    const ids = new Set();
    transcriptSegments.forEach(seg => ids.add(seg.speaker_id));
    return [...ids].sort((a, b) => a - b);
  }, [transcriptSegments]);


  const startEditing = (speakerId) => {
    setEditingSpeaker(speakerId);
    setEditValue(speakerNames[speakerId] || '');
  };

  const confirmEdit = () => {
    if (editingSpeaker !== null && editValue.trim()) {
      const updated = { ...speakerNames, [editingSpeaker]: editValue.trim() };
      setSpeakerNames(updated);
      // Persist to backend
      if (sessionId) {
        fetch(`${API_BASE_URL}/api/meeting/${sessionId}/speakers`, {
          method: 'PUT',
          headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify(updated),
        }).catch(err => console.error('Failed to save speaker names:', err));
      }
    }
    setEditingSpeaker(null);
    setEditValue('');
  };

  const handleEditKeyDown = (e) => {
    if (e.key === 'Enter') confirmEdit();
    if (e.key === 'Escape') { setEditingSpeaker(null); setEditValue(''); }
  };

  const startEditingTitle = () => {
    setEditingTitle(true);
    setTitleEditValue(sessionTitle || '');
  };

  const confirmTitleEdit = () => {
    const trimmed = titleEditValue.trim();
    if (trimmed && trimmed !== sessionTitle) {
      setSessionTitle(trimmed);
      if (sessionId) {
        fetch(`${API_BASE_URL}/api/meeting/${sessionId}/title`, {
          method: 'PUT',
          headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ title: trimmed }),
        }).catch(err => console.error('Failed to save title:', err));
      }
    }
    setEditingTitle(false);
    setTitleEditValue('');
  };

  const handleTitleKeyDown = (e) => {
    if (e.key === 'Enter') confirmTitleEdit();
    if (e.key === 'Escape') { setEditingTitle(false); setTitleEditValue(''); }
  };

  // ── Track speech activity for visualizer ──
  // Active when partialText is non-empty, or briefly after a final arrives
  const [speechActive, setSpeechActive] = useState(false);
  const speechTimeoutRef = useRef(null);
  const prevSegCountRef = useRef(0);

  // Partials: active as long as partial text is present
  useEffect(() => {
    if (partialText) {
      setSpeechActive(true);
      if (speechTimeoutRef.current) clearTimeout(speechTimeoutRef.current);
      speechTimeoutRef.current = setTimeout(() => setSpeechActive(false), 2000);
    }
    return () => { if (speechTimeoutRef.current) clearTimeout(speechTimeoutRef.current); };
  }, [partialText]);

  // Finals: keep active briefly when a new segment arrives (partial resets to '')
  useEffect(() => {
    if (transcriptSegments.length > prevSegCountRef.current && isRecording) {
      setSpeechActive(true);
      if (speechTimeoutRef.current) clearTimeout(speechTimeoutRef.current);
      speechTimeoutRef.current = setTimeout(() => setSpeechActive(false), 2000);
    }
    prevSegCountRef.current = transcriptSegments.length;
  }, [transcriptSegments.length, isRecording]);

  // ── Menu anchors for copy/download dropdowns ──
  const [copyTranscriptAnchor, setCopyTranscriptAnchor] = useState(null);
  const [dlTranscriptAnchor, setDlTranscriptAnchor] = useState(null);
  const [copyMinutesAnchor, setCopyMinutesAnchor] = useState(null);
  const [dlMinutesAnchor, setDlMinutesAnchor] = useState(null);

  // ── Speaker name resolution ──
  // Replaces "Speaker 0", "Speaker 1", etc. with mapped names in any string
  const resolveSpeakers = (text) => {
    if (!text) return text;
    return Object.entries(speakerNames).reduce(
      (t, [id, name]) => t.replace(new RegExp(`Speaker ${id}\\b`, 'g'), name),
      text
    );
  };

  // ── Format helpers ──
  const transcriptAsText = () =>
    transcriptSegments.map(seg => {
      const name = speakerNames[seg.speaker_id] || `Speaker ${seg.speaker_id}`;
      return `${name}:\n${seg.text}`;
    }).join('\n\n');

  const transcriptAsMarkdown = () =>
    transcriptSegments.map(seg => {
      const name = speakerNames[seg.speaker_id] || `Speaker ${seg.speaker_id}`;
      return `**${name}:**\n\n${seg.text}`;
    }).join('\n\n---\n\n');

  const minutesAsText = () => {
    if (!minutes) return '';
    const lines = [];
    if (minutes.summary) lines.push(`Summary\n\n${resolveSpeakers(minutes.summary)}`);
    if (minutes.topics?.length) lines.push(`Topics\n\n${minutes.topics.map(tp => `- ${resolveSpeakers(tp)}`).join('\n')}`);
    if (minutes.decisions?.length) lines.push(`Decisions\n\n${minutes.decisions.map(d => `- ${resolveSpeakers(d)}`).join('\n')}`);
    if (minutes.action_items?.length) {
      lines.push(`Action Items\n\n${minutes.action_items.map(a =>
        `- ${resolveSpeakers(a.task)}${a.owner ? ` (${resolveSpeakers(a.owner)})` : ''}${a.deadline ? ` — by ${a.deadline}` : ''}`
      ).join('\n')}`);
    }
    return lines.join('\n\n');
  };

  const minutesAsMarkdown = () => {
    if (!minutes) return '';
    const lines = [];
    if (minutes.summary) lines.push(`## Summary\n\n${resolveSpeakers(minutes.summary)}`);
    if (minutes.topics?.length) lines.push(`## Topics\n\n${minutes.topics.map(tp => `- ${resolveSpeakers(tp)}`).join('\n')}`);
    if (minutes.decisions?.length) lines.push(`## Decisions\n\n${minutes.decisions.map(d => `- ${resolveSpeakers(d)}`).join('\n')}`);
    if (minutes.action_items?.length) {
      lines.push(`## Action Items\n\n${minutes.action_items.map(a =>
        `- ${resolveSpeakers(a.task)}${a.owner ? ` (${resolveSpeakers(a.owner)})` : ''}${a.deadline ? ` — by ${a.deadline}` : ''}`
      ).join('\n')}`);
    }
    return lines.join('\n\n');
  };

  const copyToClipboard = (text) => navigator.clipboard.writeText(text);

  const downloadFile = (content, filename, mimeType) => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadAsPdf = async () => {
    if (!sessionId) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/meeting/${sessionId}/pdf`, {
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error('PDF generation failed');
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `meeting-minutes-${sessionId}.pdf`;
      a.click();
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error('PDF download error:', err);
    }
  };

  return (
    <Accordion
      expanded={modal ? true : effectiveExpanded}
      onChange={modal ? undefined : () => setIsExpanded(!isExpanded)}
      sx={modal
        ? {
            m: 0,
            background: 'transparent',
            border: 'none',
            boxShadow: 'none',
            backdropFilter: 'none',
            borderRadius: 0,
            '&:before': { display: 'none' },
            '&:hover': { background: 'transparent', borderColor: 'transparent', boxShadow: 'none' },
            '&.Mui-expanded': { background: 'transparent', border: 'none', boxShadow: 'none', margin: 0 },
          }
        : { mb: 2 }}
    >
      <AccordionSummary
        expandIcon={modal ? null : <ExpandMoreIcon sx={{ color: 'text.secondary' }} />}
        sx={{
          '& .MuiAccordionSummary-content': {
            alignItems: 'center',
            gap: 1.5,
            // In the modal: no vertical margins, just an 8px padding-bottom —
            // matching the unified modal header (Assistant Lion / ARE).
            my: modal ? 0 : '10px',
            '&.Mui-expanded': { my: modal ? 0 : '10px' },
          },
          minHeight: modal ? 'auto' : 48,
          '&.Mui-expanded': { minHeight: modal ? 'auto' : 48 },
          px: modal ? 0 : 1.5,
          ...(modal && { pt: 0, pb: 1, cursor: 'default' }),
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 32,
            height: 32,
            borderRadius: '8px',
            bgcolor: 'rgba(25, 208, 239, 0.08)',
            color: 'primary.main',
            '& .MuiSvgIcon-root': { fontSize: 18 },
          }}
        >
          {icon}
        </Box>
        <Typography sx={{ fontWeight: 400, fontSize: '15px', letterSpacing: '0.02em', flexGrow: 1 }}>
          {title}
        </Typography>
        {isRecording && (
          <Chip
            icon={<FiberManualRecordIcon sx={{ fontSize: 10, color: 'error.main' }} />}
            label="REC"
            size="small"
            variant="outlined"
            sx={{ borderColor: 'error.main', color: 'error.main', fontSize: '11px', height: 24 }}
          />
        )}
        {modal && onClose && (
          <IconButton
            onClick={() => onClose()}
            onMouseDown={(e) => e.stopPropagation()}
            size="small"
            aria-label="Close"
            sx={{ ...modalHeaderBtnSx, p: 0.75 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </AccordionSummary>

      <AccordionDetails sx={{ pt: 0, px: 1.5, pb: 1.5 }}>
        {/* Idle state with intro text */}
        {(sessionState === 'idle' || sessionState === 'completed' || sessionState === 'error') && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: 2 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {t('meeting_minutes_description') || 'Start recording to automatically transcribe a meeting and generate minutes. The AI identifies speakers and summarises the key points.'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Button
                variant="contained"
                startIcon={<FiberManualRecordIcon sx={{ fontSize: 14 }} />}
                onClick={() => { setSpeakerNames({}); startSession('multi'); }}
                size="small"
              >
                {t('meeting_minutes_start') || 'Start Recording'}
              </Button>
            </Box>
          </Box>
        )}

        {/* Connecting state */}
        {isConnecting && (
          <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center', py: 2 }}>
            <CircularProgress size={18} />
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {t('mm_connecting') || 'Connecting...'}
            </Typography>
          </Box>
        )}

        {/* Recording state */}
        {isRecording && (
          <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center' }}>
            <Button
              variant="contained"
              color="error"
              startIcon={<StopIcon />}
              onClick={() => stopSession(language, speakerNames)}
              size="small"
            >
              {t('meeting_minutes_stop') || 'Stop Recording'}
            </Button>
          </Box>
        )}

        {/* Speaker pills bar */}
        {detectedSpeakers.length > 0 && (isRecording || isRediarizing || isGenerating || isCompleted) && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1.5, alignItems: 'center' }}>
            {detectedSpeakers.map(id => {
              const color = SPEAKER_COLORS[id % SPEAKER_COLORS.length];
              const label = speakerNames[id] || `Speaker ${id}`;
              const isEditing = editingSpeaker === id;

              if (isEditing) {
                return (
                  <Box key={id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
                    <TextField
                      size="small"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onKeyDown={handleEditKeyDown}
                      onBlur={confirmEdit}
                      autoFocus
                      sx={{
                        '& .MuiInputBase-input': { fontSize: '12px', py: '3px', px: '8px', width: '100px' },
                        '& .MuiOutlinedInput-root': { borderRadius: '12px' },
                      }}
                    />
                    <CheckIcon
                      sx={{ fontSize: 16, color: 'success.main', cursor: 'pointer' }}
                      onClick={confirmEdit}
                    />
                  </Box>
                );
              }

              return (
                <Chip
                  key={id}
                  label={label}
                  size="small"
                  onClick={() => startEditing(id)}
                  onDelete={() => startEditing(id)}
                  deleteIcon={<EditIcon sx={{ fontSize: '13px !important', color: `${color} !important`, opacity: 0.6, '&:hover': { opacity: 1 } }} />}
                  sx={{
                    bgcolor: `${color}18`,
                    color: color,
                    fontSize: '12px',
                    fontWeight: 500,
                    height: 26,
                    border: 'none',
                    '& .MuiChip-icon': { color },
                  }}
                  icon={<Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: color, ml: '6px !important' }} />}
                />
              );
            })}
          </Box>
        )}

        {/* Error */}
        {error && (
          <Typography variant="body2" color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}

        {/* Recording: listening visualizer + word ticker */}
        {isRecording && (
          <Box sx={{
            mb: 2,
            py: 3,
            px: 2,
            bgcolor: 'background.default',
            borderRadius: '12px',
            border: 1,
            borderColor: 'divider',
          }}>
            <ListeningVisualizer active={speechActive || isRecording} />
            <WordTicker text={partialText} />
          </Box>
        )}

        {/* Processing: rediarizing or generating */}
        {(isRediarizing || isGenerating) && (
          <ProcessingOverlay state={isRediarizing ? 'rediarizing' : 'generating'} />
        )}

        {/* Editable meeting title (shown after minutes are generated) */}
        {isCompleted && sessionTitle && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5 }}>
            {editingTitle ? (
              <>
                <TextField
                  size="small"
                  value={titleEditValue}
                  onChange={(e) => setTitleEditValue(e.target.value)}
                  onKeyDown={handleTitleKeyDown}
                  onBlur={confirmTitleEdit}
                  autoFocus
                  fullWidth
                  sx={{
                    '& .MuiInputBase-input': { fontSize: '14px', py: '4px', px: '8px', fontWeight: 600 },
                    '& .MuiOutlinedInput-root': { borderRadius: '8px' },
                  }}
                />
                <CheckIcon
                  sx={{ fontSize: 18, color: 'success.main', cursor: 'pointer', flexShrink: 0 }}
                  onClick={confirmTitleEdit}
                />
              </>
            ) : (
              <>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '14px', flexGrow: 1 }}>
                  {sessionTitle}
                </Typography>
                <IconButton size="small" onClick={startEditingTitle} sx={{ p: 0.25 }}>
                  <EditIcon sx={{ fontSize: 14, color: 'text.secondary', opacity: 0.5, '&:hover': { opacity: 1 } }} />
                </IconButton>
              </>
            )}
          </Box>
        )}

        {/* Completed: full transcript with copy/download */}
        {isCompleted && transcriptSegments.length > 0 && (
          <Box sx={{
            mb: 2,
            animation: 'transcriptReveal 0.6s ease',
            '@keyframes transcriptReveal': {
              from: { opacity: 0, transform: 'translateY(8px)' },
              to: { opacity: 1, transform: 'translateY(0)' },
            },
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary',
                textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
                {t('mm_transcript') || 'Transcript'}
              </Typography>
              <>
                <Tooltip title={t('mm_copy') || 'Copy'}>
                  <IconButton size="small" onClick={e => setCopyTranscriptAnchor(e.currentTarget)} sx={{ ml: 0.5, p: 0.25 }}>
                    <ContentCopyIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                  </IconButton>
                </Tooltip>
                <Menu anchorEl={copyTranscriptAnchor} open={Boolean(copyTranscriptAnchor)} onClose={() => setCopyTranscriptAnchor(null)}>
                  <MenuItem onClick={() => { copyToClipboard(transcriptAsText()); setCopyTranscriptAnchor(null); }}>
                    <ListItemIcon><TextSnippetIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>Plain text</ListItemText>
                  </MenuItem>
                  <MenuItem onClick={() => { copyToClipboard(transcriptAsMarkdown()); setCopyTranscriptAnchor(null); }}>
                    <ListItemIcon><CodeIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>Markdown</ListItemText>
                  </MenuItem>
                </Menu>
                <Tooltip title={t('mm_download') || 'Download'}>
                  <IconButton size="small" onClick={e => setDlTranscriptAnchor(e.currentTarget)} sx={{ p: 0.25 }}>
                    <DownloadIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                  </IconButton>
                </Tooltip>
                <Menu anchorEl={dlTranscriptAnchor} open={Boolean(dlTranscriptAnchor)} onClose={() => setDlTranscriptAnchor(null)}>
                  <MenuItem onClick={() => { downloadFile(transcriptAsText(), 'transcript.txt', 'text/plain'); setDlTranscriptAnchor(null); }}>
                    <ListItemIcon><TextSnippetIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>Plain text (.txt)</ListItemText>
                  </MenuItem>
                  <MenuItem onClick={() => { downloadFile(transcriptAsMarkdown(), 'transcript.md', 'text/markdown'); setDlTranscriptAnchor(null); }}>
                    <ListItemIcon><CodeIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>Markdown (.md)</ListItemText>
                  </MenuItem>
                  <MenuItem onClick={() => { downloadAsPdf(); setDlTranscriptAnchor(null); }}>
                    <ListItemIcon><PictureAsPdfIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>PDF</ListItemText>
                  </MenuItem>
                </Menu>
              </>
            </Box>
            <TranscriptView
              segments={transcriptSegments}
              partialText=""
              speakerNames={speakerNames}
              version={transcriptVersion}
            />
          </Box>
        )}

        {/* Minutes */}
        {minutes && (
          <Box sx={{ mt: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary',
                textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
                {t('meeting_minutes') || 'Meeting Minutes'}
              </Typography>
              <Tooltip title={t('mm_copy') || 'Copy'}>
                <IconButton size="small" onClick={e => setCopyMinutesAnchor(e.currentTarget)} sx={{ p: 0.25 }}>
                  <ContentCopyIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                </IconButton>
              </Tooltip>
              <Menu anchorEl={copyMinutesAnchor} open={Boolean(copyMinutesAnchor)} onClose={() => setCopyMinutesAnchor(null)}>
                <MenuItem onClick={() => { copyToClipboard(minutesAsText()); setCopyMinutesAnchor(null); }}>
                  <ListItemIcon><TextSnippetIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>Plain text</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => { copyToClipboard(minutesAsMarkdown()); setCopyMinutesAnchor(null); }}>
                  <ListItemIcon><CodeIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>Markdown</ListItemText>
                </MenuItem>
              </Menu>
              <Tooltip title={t('mm_download') || 'Download'}>
                <IconButton size="small" onClick={e => setDlMinutesAnchor(e.currentTarget)} sx={{ p: 0.25 }}>
                  <DownloadIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                </IconButton>
              </Tooltip>
              <Menu anchorEl={dlMinutesAnchor} open={Boolean(dlMinutesAnchor)} onClose={() => setDlMinutesAnchor(null)}>
                <MenuItem onClick={() => { downloadFile(minutesAsText(), 'meeting-minutes.txt', 'text/plain'); setDlMinutesAnchor(null); }}>
                  <ListItemIcon><TextSnippetIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>Plain text (.txt)</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => { downloadFile(minutesAsMarkdown(), 'meeting-minutes.md', 'text/markdown'); setDlMinutesAnchor(null); }}>
                  <ListItemIcon><CodeIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>Markdown (.md)</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => { downloadAsPdf(); setDlMinutesAnchor(null); }}>
                  <ListItemIcon><PictureAsPdfIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>PDF</ListItemText>
                </MenuItem>
              </Menu>
            </Box>

            <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: '8px',
              border: 1, borderColor: 'divider' }}>
              {minutes.summary && (
                <Typography variant="body2" sx={{ mb: 2 }}>{resolveSpeakers(minutes.summary)}</Typography>
              )}

              {minutes.topics?.length > 0 && (
                <Box sx={{ mb: 1.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>{t('mm_topics') || 'Topics'}</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                    {minutes.topics.map((topic, i) => <Chip key={i} label={resolveSpeakers(topic)} size="small" variant="outlined" />)}
                  </Box>
                </Box>
              )}

              {minutes.decisions?.length > 0 && (
                <Box sx={{ mb: 1.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>{t('mm_decisions') || 'Decisions'}</Typography>
                  {minutes.decisions.map((d, i) => (
                    <Typography key={i} variant="body2" sx={{ ml: 1, mt: 0.5 }}>• {resolveSpeakers(d)}</Typography>
                  ))}
                </Box>
              )}

              {minutes.action_items?.length > 0 && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>{t('mm_action_items') || 'Action Items'}</Typography>
                  {minutes.action_items.map((a, i) => (
                    <Typography key={i} variant="body2" sx={{ ml: 1, mt: 0.5 }}>
                      • {resolveSpeakers(a.task)}{a.owner ? ` (${resolveSpeakers(a.owner)})` : ''}{a.deadline ? ` — by ${a.deadline}` : ''}
                    </Typography>
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        )}

        {/* Session history — fully isolated from active recording */}
        <SessionHistory activeSessionId={sessionId} />
      </AccordionDetails>
    </Accordion>
  );
}
